import { AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from './ui/dialog';

/**
 * ErrorDialog - A blocking error popup with field highlighting
 * 
 * Props:
 * - open: boolean - whether dialog is open
 * - onClose: function(shouldHighlight: boolean) - callback when dialog closes
 * - message: string - error message to display
 * - fieldName: string - name of the field with error (for display)
 * - fieldId: string - ID of the input element to highlight (optional)
 */
export default function ErrorDialog({ open, onClose, message, fieldName, fieldId }) {
  const handleOkClick = () => {
    // Close the dialog
    onClose(false);
    
    // If fieldId is provided, highlight and focus the field
    if (fieldId) {
      setTimeout(() => {
        const element = document.getElementById(fieldId) || document.querySelector(`[data-testid="${fieldId}"]`);
        if (element) {
          // Add error styling
          element.classList.add('border-red-500', 'ring-2', 'ring-red-200');
          element.focus();
          
          // Remove error styling after 5 seconds
          setTimeout(() => {
            element.classList.remove('border-red-500', 'ring-2', 'ring-red-200');
          }, 5000);
        }
      }, 100);
    }
  };

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent className="sm:max-w-md" data-testid="error-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
              <AlertCircle className="w-6 h-6" />
            </div>
            <span>Validation Error</span>
          </DialogTitle>
        </DialogHeader>
        <div className="py-4">
          <p className="text-slate-700 text-base">{message}</p>
          {fieldName && (
            <p className="text-sm text-slate-500 mt-3 p-3 bg-red-50 rounded-lg">
              Please check the <span className="font-semibold text-red-600">{fieldName}</span> field.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button 
            onClick={handleOkClick} 
            className="w-full bg-red-600 hover:bg-red-700 text-white rounded-xl h-11"
            data-testid="error-dialog-ok"
          >
            OK
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
